import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import MemberAutocomplete from "@/components/finance/MemberAutocomplete";
import { usePermissions } from "@/hooks/usePermissions";
import { sortGeneralFirstByName } from "@/lib/utils";

const BASE_INCOME_CATEGORIES = ["Tithes", "Offerings", "Donations", "Other"];
const BASE_EXPENSE_CATEGORIES = ["Utilities", "Salaries", "Maintenance", "Supplies", "Missions", "Events", "Rent/Mortgage", "Insurance", "Transportation", "Other"];
const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Mobile Money", "Card", "Cheque", "Other"] as const;

const sortCategoriesWithOtherLast = (categories: string[]) => {
  return [...categories].sort((a, b) => {
    const aIsOther = a.trim().toLowerCase() === "other";
    const bIsOther = b.trim().toLowerCase() === "other";
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;
    return a.localeCompare(b);
  });
};

const sortIncomeCategoriesWithPriority = (categories: string[]) => {
  const priority: Record<string, number> = {
    tithes: 0,
    offerings: 1,
  };
  return [...categories].sort((a, b) => {
    const aRank = priority[a.trim().toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
    const bRank = priority[b.trim().toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
};

interface FinanceRecord {
  id: string; member_name: string | null; amount: number; date: string;
  category: string; payment_method: string | null; notes: string | null;
  member_id: string | null;
  created_by: string | null; created_at: string; updated_at: string;
}

type Organization = {
  id: string;
  name: string;
  type: string;
  level: string;
  parent_id: string | null;
};

interface Props { open: boolean; onClose: () => void; record: FinanceRecord | null; }

const defaultForm = () => ({ member_name: "", member_id: null as string | null, amount: "", date: new Date().toISOString().split("T")[0], category: "" as string, payment_method: "" as string, notes: "", record_type: "income" as string });

const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";
const parseOrganizationFromNotes = (notes: string | null) => {
  if (!notes) return { organization: "", organizationIds: [] as string[], expenseItem: "", cleanNotes: "" };
  const lines = notes.split("\n");
  const firstLine = lines.find((line) => {
    const trimmed = line.trim();
    return trimmed.startsWith(MINISTRY_NOTE_PREFIX) || trimmed.startsWith(LEGACY_ORG_NOTE_PREFIX);
  })?.trim() || "";
  const idsLine = lines.find((line) => {
    const trimmed = line.trim();
    return trimmed.startsWith(MINISTRY_IDS_NOTE_PREFIX) || trimmed.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX);
  })?.trim() || "";
  const cleanLines = lines.filter((line) => {
    const trimmed = line.trim();
    return !trimmed.startsWith(MINISTRY_NOTE_PREFIX)
      && !trimmed.startsWith(LEGACY_ORG_NOTE_PREFIX)
      && !trimmed.startsWith(MINISTRY_IDS_NOTE_PREFIX)
      && !trimmed.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX);
  });
  return {
    organization: firstLine
      .replace(MINISTRY_NOTE_PREFIX, "")
      .replace(LEGACY_ORG_NOTE_PREFIX, "")
      .trim(),
    organizationIds: idsLine
      ? idsLine
          .replace(MINISTRY_IDS_NOTE_PREFIX, "")
          .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
          .split(">").map((part) => part.trim()).filter(Boolean)
      : [],
    cleanNotes: cleanLines.join("\n").trim(),
  };
};

export default function FinanceFormDialog({ open, onClose, record }: Props) {
  const queryClient = useQueryClient();
  const { user, role } = useAuth();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess } = usePermissions();
  const { profile } = useChurch();
  const isEdit = !!record;
  const [form, setForm] = useState(defaultForm());
  const [selectedOrgId, setSelectedOrgId] = useState("");

  // Date calendar popover
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      update("date", `${yyyy}-${mm}-${dd}`);
      setCalendarOpen(false);
    }
  };

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, type, level, parent_id")
            .eq("church_id", profile!.id)
            .order("name"); // Original ordering
      if (error) throw error;
          return sortGeneralFirstByName((data || []) as Organization[]); // Updated to sort
    },
    enabled: !!profile?.id,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members", "finance_form_dialog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
  });

  const memberIdByLowerName = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((member: any) => {
      const normalizedName = String(member.full_name || "").trim().toLowerCase();
      if (normalizedName && member.id && !map.has(normalizedName)) {
        map.set(normalizedName, member.id);
      }
    });
    return map;
  }, [members]);

  const financeScoped = role !== "admin" && hasFinanceScopedAccess();
  const allowedFinanceOrgIds = getFinanceOrganizationIds();

  const accessibleOrgSet = useMemo(() => {
    if (!financeScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedFinanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [financeScoped, allowedFinanceOrgIds, organizations]);

  const accessibleOrganizations = useMemo(() => {
    if (!financeScoped || !accessibleOrgSet) return organizations;
    return organizations.filter((org) => accessibleOrgSet.has(org.id));
  }, [organizations, financeScoped, accessibleOrgSet]);

  const selectableMinistries = useMemo(
    () => sortGeneralFirstByName(accessibleOrganizations.filter((org) => org.type === "ministry" || !org.type)),
    [accessibleOrganizations]
  );

  // Load church-specific expense categories from DB
  const { data: dbExpenseCategories = [] } = useQuery({
    queryKey: ["expense_categories", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("name")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return data.map((d: any) => d.name as string);
    },
    enabled: !!profile?.id,
  });

  // Load custom categories from annual_budgets
  const { data: budgetCustomCats = [] } = useQuery({
    queryKey: ["budget_custom_cats", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("annual_budgets")
        .select("category, record_type")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return (data || []) as { category: string; record_type: string }[];
    },
    enabled: !!profile?.id,
  });

  const baseIncomeSet = useMemo(() => new Set(BASE_INCOME_CATEGORIES), []);
  const baseExpenseSet = useMemo(() => new Set(BASE_EXPENSE_CATEGORIES), []);

  const allExpenseCategories = useMemo(() => {
    const budgetExpense = budgetCustomCats
      .filter((b) => b.record_type === "expense" && !baseExpenseSet.has(b.category))
      .map((b) => b.category);
    return sortCategoriesWithOtherLast([...new Set([...BASE_EXPENSE_CATEGORIES, ...dbExpenseCategories, ...budgetExpense])]);
  }, [dbExpenseCategories, budgetCustomCats, baseExpenseSet]);

  const allIncomeCategories = useMemo(() => {
    const budgetIncome = budgetCustomCats
      .filter((b) => b.record_type === "income" && !baseIncomeSet.has(b.category))
      .map((b) => b.category);
    return sortIncomeCategoriesWithPriority(sortCategoriesWithOtherLast([...new Set([...BASE_INCOME_CATEGORIES, ...budgetIncome])]));
  }, [budgetCustomCats, baseIncomeSet]);

  const CATEGORIES = form.record_type === "expense" ? allExpenseCategories : allIncomeCategories;

  // Reset form only when dialog opens/closes or record changes
  useEffect(() => {
    if (record) {
      const parsed = parseOrganizationFromNotes(record.notes);
      setForm({ member_name: record.member_name || "", member_id: record.member_id || null, amount: Number(record.amount).toLocaleString('en-US'), date: record.date, category: record.category, payment_method: record.payment_method || "", notes: parsed.cleanNotes || "", record_type: (record as any).record_type || "income" });
      if (record.date) {
        setSelectedDate(parseISO(record.date));
      }
    } else {
      setForm(defaultForm());
      setSelectedDate(new Date());
      setSelectedOrgId("");
    }
  }, [record, open]);

  // Hydrate selected ministry when organizations load or record changes
  useEffect(() => {
    if (!record || selectableMinistries.length === 0) return;
    const parsed = parseOrganizationFromNotes(record.notes);

    if (parsed.organizationIds.length > 0) {
      const existingId = [...parsed.organizationIds]
        .reverse()
        .find((id) => selectableMinistries.some((org) => org.id === id));
      if (existingId) {
        setSelectedOrgId(existingId);
        return;
      }
    }

    const names = parsed.organization
      .split(">")
      .map((part) => part.trim())
      .filter(Boolean);

    if (names.length === 0) {
      setSelectedOrgId("");
      return;
    }

    const lastName = names[names.length - 1];
    const byName = selectableMinistries.find((org) => org.name.toLowerCase() === lastName.toLowerCase());
    setSelectedOrgId(byName?.id || "");
  }, [record, open, selectableMinistries]);

  const mutation = useMutation({
    mutationFn: async () => {
      const selectedOrganization = selectableMinistries.find((org) => org.id === selectedOrgId);
      const selectedOrganizationPath = selectedOrganization?.name || "";
      const selectedOrganizationIds = selectedOrganization?.id || "";
      const resolvedMemberId = form.member_id || memberIdByLowerName.get(form.member_name.trim().toLowerCase()) || null;
      const noteBody = form.notes.trim();
      const notesWithOrganization = [
        selectedOrganizationPath ? `${MINISTRY_NOTE_PREFIX}${selectedOrganizationPath}` : "",
        selectedOrganizationPath && selectedOrganizationIds ? `${MINISTRY_IDS_NOTE_PREFIX}${selectedOrganizationIds}` : "",
        noteBody,
      ].filter(Boolean).join("\n") || null;

      const payload: any = {
        member_name: form.member_name.trim() || null, amount: parseFloat(form.amount.replace(/,/g, '')),
        member_id: resolvedMemberId,
        date: form.date, category: form.category, payment_method: form.payment_method || null,
        notes: notesWithOrganization, created_by: user?.id || null, record_type: form.record_type,
      };
      if (isEdit) {
        const { error } = await supabase.from("finance_records").update(payload).eq("id", record.id);
        if (error) throw error;
      } else {
        payload.church_id = profile?.id;
        const { error } = await supabase.from("finance_records").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["finance_records"] }); queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });  },
    onError: (err: Error) => toast.error(err.message),
  });

  const validate = () => {
    if (!profile?.id) { toast.error("Church profile not found. Please refresh and try again."); return false; }
    if (!form.amount || isNaN(parseFloat(form.amount.replace(/,/g, ''))) || parseFloat(form.amount.replace(/,/g, '')) <= 0) { toast.error("Please enter a valid amount"); return false; }
    if (!form.category) { toast.error("Please select a category"); return false; }
    if (financeScoped && !selectedOrgId) { toast.error("Please select an allowed ministry"); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); if (!validate()) return; await mutation.mutateAsync(); onClose(); };
  const handleSaveAndAddAnother = async () => { if (!validate()) return; await mutation.mutateAsync(); setForm((prev) => ({ ...defaultForm(), date: prev.date, category: prev.category })); };
  const update = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading">{isEdit ? "Edit Finance Record" : form.record_type === "expense" ? "Record Expense" : "Record Income"}</SheetTitle></SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2"><Label>Record Type *</Label>
              <Select value={form.record_type} onValueChange={(v) => { update("record_type", v); update("category", ""); }}>
                <SelectTrigger
                  className={cn(
                    form.record_type === "expense"
                      ? "border-destructive/40 bg-destructive/5 text-destructive"
                      : "border-success/40 bg-success/5 text-success"
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Amount *</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span><Input type="text" inputMode="numeric" placeholder="0" value={form.amount} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ''); const parts = raw.split('.'); const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); update('amount', parts.length > 1 ? `${int}.${parts[1]}` : int); }} className="pl-7" required autoFocus={!isEdit} /></div></div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.date && "text-muted-foreground")}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {form.date ? format(parseISO(form.date), "MMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2"><Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Ministry</Label>
              <Select value={selectedOrgId || "__none"} onValueChange={(v) => setSelectedOrgId(v === "__none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select ministry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {selectableMinistries.map((org) => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}><SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            {form.record_type === "income" && (
              <div className="col-span-2 space-y-2"><Label>Source (Giver)</Label><MemberAutocomplete value={form.member_name} onChange={(v) => setForm((prev) => ({ ...prev, member_name: v, member_id: null }))} onMemberSelect={(member) => setForm((prev) => ({ ...prev, member_name: member?.full_name || prev.member_name, member_id: member?.id || null }))} options={members as { id: string; full_name: string }[]} placeholder="Start typing to search members..." /></div>
            )}
            <div className="col-span-2 space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="Optional notes..." /></div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <div className="ml-auto flex items-center gap-3">
              {!isEdit && <Button type="button" variant="secondary" onClick={handleSaveAndAddAnother} disabled={mutation.isPending}><Plus className="w-4 h-4 mr-1" />Save & Add Another</Button>}
              <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Saving..." : isEdit ? "Update" : "Record Income"}</Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
